/**
 * 访客星球地图的展示规则（纯函数，页面与用例共用）。只把公开图层的事实画出来，不猜、不补、不挪：
 * - 有 position 的才上地图；position 为 null 的不画点、不塞到香港或零经纬度，进“暂时不知道 TA 在哪”；
 * - 聚合只按坐标相等（四类 area 按 grid_deg 吸附；登记住宅保留代表点，不吸附；分组键与 world_map/homeCluster.homeKey 同一个 toFixed(6)）：
 *   同一处的几位合成一个标记，点开由页面列名单再看短卡（总览这样）；前端不按屏幕距离合并；
 *   地区层（城市地图）里坐标完全相同的几位按宠物 id 在格心周围稳定错开、不出这一格（spreadClusters，UI-REVIEW110-04，I 10-14：
 *   公开层按 0.01° 格子粗化，2531 走查发现西安三只出门去了三处却画成一点、香港阿澄去的花店和驿站同一格像没出门）；
 * - 同一视图里同名的居民，名字后面带城市（withCityOnSameName，I 10-14，不改名）；
 * - last_known_area 只说“上次在这一带”，不当成此刻的位置；四类 area 位置都是片区近似值，说“大约在……一带”；登记住宅点只说明按登记小区标示，不写门牌、座位。
 * 坐标系：公开图层是 WGS-84（coord_system 缺省 "wgs84"）。示意图（2D 占位总览、地区层的 shared/map 示意底图）都直接按 WGS-84 做
 * Web Mercator 投影；只有将来画到高德上才要先换成 GCJ-02（world_map/coords.toAmapLngLat）。
 */
import type { LatLng, PublicMapResident } from "@/shared/contracts";
import { homeKey } from "@/features/world_map/homeCluster";
import type { GlobeCluster, GlobeRegion } from "./globeTypes";

export function hasPosition(resident: PublicMapResident): resident is PublicMapResident & { position: NonNullable<PublicMapResident["position"]> } {
  const p = resident.position;
  return Boolean(p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180);
}

/**
 * 同一格里错开的半径（度）：小于半格 0.005°，不出格。
 * 尽量往外放：共用地图的投影把经度跨度最少按 0.02° 算（shared/map），一格在屏幕上最多约 135px 宽，挤在格心就分不开。
 */
export const CELL_SPREAD_DEG = 0.0045;
/** 一圈最多排几位；再多的往里一圈圈收（向日葵式），挤在一起时由屏幕合成、放大能分开 */
export const CELL_RING_MAX = 8;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * 同一格里第 index 位（共 count 位，按宠物 id 排好）摆在哪；只有一位就在格心。
 * 不超过 CELL_RING_MAX 位：等距排在半径 CELL_SPREAD_DEG 的一圈上，从正上方起顺时针；更多时向日葵式，半径不超过 CELL_SPREAD_DEG。
 */
export function spreadInCell(at: LatLng, index: number, count: number): LatLng {
  if (count <= 1) return at;
  const [r, theta] = count <= CELL_RING_MAX ? [CELL_SPREAD_DEG, (index / count) * 2 * Math.PI] : [CELL_SPREAD_DEG * Math.sqrt((index + 0.5) / count), index * GOLDEN_ANGLE];
  return { lat: at.lat + r * Math.cos(theta), lng: at.lng + r * Math.sin(theta) };
}

/**
 * 地区层用：一处有几位（坐标完全相同，同一格）就按宠物 id 错开成几处，各一位；一位的照旧（key、坐标都不变）。
 * 错开后的 key 是「那一处的 key~宠物 id」：同一份数据每次算出来都一样；拿那一处的 key 找时，前缀对得上。
 */
export function spreadClusters(clusters: readonly GlobeCluster[]): GlobeCluster[] {
  return clusters.flatMap((cell) => {
    if (cell.residents.length === 1) return [cell];
    const sorted = [...cell.residents].sort((a, b) => (a.pet_id < b.pet_id ? -1 : a.pet_id > b.pet_id ? 1 : 0));
    return sorted.map((resident, index) => {
      const at = spreadInCell(cell, index, sorted.length);
      return { key: `${cell.key}~${resident.pet_id}`, lat: at.lat, lng: at.lng, residents: [resident] };
    });
  });
}

/** 按 key 找那一处：先找完全一样的；找不到再找「这个 key~宠物 id」错开出来的第一处（总览上记下的格心 key，到地区层已错开） */
export function clusterByKey(clusters: readonly GlobeCluster[], key: string): GlobeCluster | null {
  return clusters.find((cluster) => cluster.key === key) ?? clusters.find((cluster) => cluster.key.startsWith(`${key}~`)) ?? null;
}

/** 同一视图里同名的居民（按宠物 id 算人），名字后面带城市：「栗子（西安）」；没有城市的照旧。不改名，只改这一屏怎么叫 */
export function withCityOnSameName(residents: readonly PublicMapResident[]): PublicMapResident[] {
  const people = new Map<string, Set<string>>();
  for (const resident of residents) {
    const name = resident.name.trim();
    people.set(name, (people.get(name) ?? new Set<string>()).add(resident.pet_id));
  }
  return residents.map((resident) => {
    const name = resident.name.trim();
    const city = resident.city?.trim();
    return (people.get(name)?.size ?? 0) > 1 && city ? { ...resident, name: `${name}（${city}）` } : resident;
  });
}

/** 按公开坐标分组（坐标完全相等才算同一处），按图层原来的先后。没有 position 的不进来。 */
export function clustersOf(residents: readonly PublicMapResident[]): GlobeCluster[] {
  const clusters = new Map<string, GlobeCluster>();
  for (const resident of residents) {
    if (!hasPosition(resident)) continue;
    const at = { lat: resident.position.lat, lng: resident.position.lng };
    const key = homeKey(at);
    const cluster = clusters.get(key);
    if (cluster) cluster.residents.push(resident);
    else clusters.set(key, { key, lat: at.lat, lng: at.lng, residents: [resident] });
  }
  return [...clusters.values()];
}

export function unplaced(residents: readonly PublicMapResident[]): PublicMapResident[] {
  return residents.filter((resident) => !hasPosition(resident));
}

/** 地图上写的城市名：每座城市一个，放在这座城市各处的重心（同一份坐标算出来的，不另配位置）。在路上（city 为 null）的不算。 */
export function cityLabels(clusters: readonly GlobeCluster[]): Array<{ city: string; at: LatLng }> {
  const groups = new Map<string, LatLng[]>();
  for (const cluster of clusters) {
    for (const city of new Set(cluster.residents.map((resident) => resident.city?.trim()).filter((c): c is string => Boolean(c)))) {
      groups.set(city, [...(groups.get(city) ?? []), { lat: cluster.lat, lng: cluster.lng }]);
    }
  }
  return [...groups].map(([city, points]) => ({
    city,
    at: { lat: points.reduce((s, p) => s + p.lat, 0) / points.length, lng: points.reduce((s, p) => s + p.lng, 0) / points.length },
  }));
}

/** 一个地区框里的各处（中心 ± 跨度的一半；经度跨 180 度时按绕回算）。 */
export function clustersIn(clusters: readonly GlobeCluster[], region: GlobeRegion): GlobeCluster[] {
  const halfLat = region.latSpan / 2;
  const halfLng = region.lngSpan / 2;
  return clusters.filter((cluster) => {
    const dLng = Math.abs(((cluster.lng - region.center.lng + 540) % 360) - 180);
    return Math.abs(cluster.lat - region.center.lat) <= halfLat && dLng <= halfLng;
  });
}

/**
 * 地区层先看哪几处：框里离中心最近的那一处所在的城市（城市说不清就取它周围 1 度以内）。
 * 星球组件交来的框可能很大（缺省 25 度，会把香港和西安框在一起）：镜头只装这一座城市，别的城市照样画在图上、拖过去就看得到。
 */
export function regionFocus(clusters: readonly GlobeCluster[], region: GlobeRegion): GlobeCluster[] {
  const inside = clustersIn(clusters, region);
  if (!inside.length) return [];
  const { lat, lng } = region.center;
  const distance = (c: GlobeCluster) => Math.hypot(c.lat - lat, (c.lng - lng) * Math.cos((lat * Math.PI) / 180));
  const nearest = inside.reduce((best, c) => (distance(c) < distance(best) ? c : best));
  const city = cityOfCluster(nearest);
  return inside.filter((c) => (city ? cityOfCluster(c) === city : Math.abs(c.lat - nearest.lat) <= 1 && Math.abs(c.lng - nearest.lng) <= 1));
}

/** 地区层的镜头要装进的点：regionFocus 那几处；框里一个都没有就装整个框。 */
export function regionBounds(clusters: readonly GlobeCluster[], region: GlobeRegion): LatLng[] {
  const focus = regionFocus(clusters, region);
  if (focus.length) return focus.map((cluster) => ({ lat: cluster.lat, lng: cluster.lng }));
  const { center, latSpan, lngSpan } = region;
  return [
    { lat: center.lat - latSpan / 2, lng: center.lng - lngSpan / 2 },
    { lat: center.lat + latSpan / 2, lng: center.lng + lngSpan / 2 },
  ];
}

/** 地区的叫法：镜头装的那几处都在同一座城市就写城市名；说不清写“这一带”。 */
export function regionLabel(clusters: readonly GlobeCluster[], region: GlobeRegion): string {
  const cities = [...new Set(regionFocus(clusters, region).flatMap((cluster) => cluster.residents.map((r) => r.city?.trim()).filter((c): c is string => Boolean(c))))];
  return cities.length === 1 ? cities[0] : "这一带";
}

/**
 * 公开地点怎么说（登记住宅代表点或片区近似，不写门牌）：
 * - 没有位置：暂时不知道 TA 在哪；
 * - 上次的位置（last_known_area）：上次在这一带——不说成此刻；
 * - 驿站一带 / 到访地点一带 / 路上（按路线估算）。
 */
export function placeText(resident: Pick<PublicMapResident, "position" | "residence" | "place_name" | "city">): string {
  const p = resident.position;
  const city = resident.city?.trim() || null;
  const inCity = city ? `（${city}）` : "";
  if (!p) return "暂时不知道 TA 在哪";
  switch (p.basis) {
    case "residential_site":
      return `在家，位置按登记小区标示${inCity}`;
    case "last_known_area":
      return `上次在这一带${inCity}，此刻在哪说不准`;
    case "residence_area":
      return `大约在${resident.residence.trim()}一带${city && !resident.residence.includes(city) ? inCity : ""}`;
    case "place_area":
      return resident.place_name?.trim() ? `大约在${resident.place_name.trim()}一带${inCity}` : `大约在${city ?? "一处公开地点"}附近`;
    case "route_area":
      return `在路上，大约在这一带${inCity}（按路线估算）`;
    default:
      return `大约在这一带${inCity}`;
  }
}

export function isLastKnown(resident: Pick<PublicMapResident, "position">): boolean {
  return resident.position?.basis === "last_known_area";
}

/** 有形象的居民（6c2b-no-illustration：没形象的居民不显示）：官方形象、身份图首选或退路，有一个就算有。 */
export function withImage(residents: readonly PublicMapResident[]): PublicMapResident[] {
  return residents.filter((r) => Boolean(r.avatar_url?.trim() || r.identity?.url || r.identity?.fallback_url));
}

/**
 * 高亮只留在有标记的地方：事件的片区格子上（或半格以内）有一处居民标记才留。地图上没有标记、或那一带没人，就不画圈
 * （第三轮：空地图左下角冒出一个孤立的圈）。从新鲜事点进地区层的“发生在这一带”另有标记，不走这里。
 */
export function highlightsOnMarkers<T extends { lat: number; lng: number }>(highlights: readonly T[], clusters: readonly GlobeCluster[]): T[] {
  const HALF_CELL = 0.006;
  return highlights.filter((h) => clusters.some((c) => Math.abs(c.lat - h.lat) <= HALF_CELL && Math.abs(c.lng - h.lng) <= HALF_CELL));
}

type Box = { left: number; right: number; top: number; bottom: number };

/**
 * 示意图上的避让分组（第三轮）：每一处在屏幕上占的框——头像 44 + 小尾巴 10 在坐标上方，同一处几位多一个人数角标，
 * “上次在这”的牌子再往上 22px、宽约 60px。框（留 2px）互相压住的几处分到一组；按人多的先放，组里第一处就是画标记的那一处。
 * 只管怎么画，不改坐标、不改数据。
 */
export function screenGroups(clusters: readonly GlobeCluster[], project: (at: { lat: number; lng: number }) => { x: number; y: number }, hasNote: (cluster: GlobeCluster) => boolean): GlobeCluster[][] {
  const boxesOf = (cluster: GlobeCluster): Box[] => {
    const { x, y } = project(cluster);
    const boxes: Box[] = [{ left: x - 24, right: x + (cluster.residents.length > 1 ? 30 : 24), top: y - (cluster.residents.length > 1 ? 60 : 56), bottom: y }];
    if (hasNote(cluster)) boxes.push({ left: x - 32, right: x + 32, top: y - 84, bottom: y - 58 });
    return boxes;
  };
  return groupByBoxes(clusters, boxesOf);
}

/**
 * 总览（2D 占位）上的避让分组（第四轮）：人数点是以坐标为中心的 44×44 圆（三位数再宽一点）。远看几处会整个叠在一起，
 * 压在下面的点不但看不见、也点不到（c84a D0842-2：点按区不能盖住地图上的点）——压住的几处合成一个点，写一共几位。
 * 3D 星球（6796 的 groupOnScreen）也是这样合的。只管怎么画，不改坐标、不改数据。
 */
export function dotGroups(clusters: readonly GlobeCluster[], project: (at: { lat: number; lng: number }) => { x: number; y: number }): GlobeCluster[][] {
  return groupByBoxes(clusters, (cluster) => {
    const { x, y } = project(cluster);
    const half = cluster.residents.length >= 100 ? 26 : 22;
    return [{ left: x - half, right: x + half, top: y - 22, bottom: y + 22 }];
  });
}

/** 框（留 2px）互相压住的几处分到一组；按人多的先放，组里第一处就是画标记的那一处。 */
function groupByBoxes(clusters: readonly GlobeCluster[], boxesOf: (cluster: GlobeCluster) => Box[]): GlobeCluster[][] {
  const hit = (a: Box, b: Box) => a.left < b.right + 2 && b.left < a.right + 2 && a.top < b.bottom + 2 && b.top < a.bottom + 2;
  const order = [...clusters].sort((a, b) => b.residents.length - a.residents.length);
  const groups: Array<{ members: GlobeCluster[]; boxes: Box[] }> = [];
  for (const cluster of order) {
    const boxes = boxesOf(cluster);
    const group = groups.find((g) => g.boxes.some((gb) => boxes.some((b) => hit(gb, b))));
    if (group) {
      group.members.push(cluster);
      group.boxes.push(...boxes);
    } else {
      groups.push({ members: [cluster], boxes });
    }
  }
  return groups.map((g) => g.members);
}

/** 合成的那一处：画在组里第一处（人最多的那一处）的真实坐标上，名单是这几处的全部居民；只有一处就原样。 */
export function mergedCluster(group: readonly GlobeCluster[]): GlobeCluster {
  if (group.length === 1) return group[0];
  const anchor = group[0];
  return { key: `merged:${group.map((c) => c.key).join("|")}`, lat: anchor.lat, lng: anchor.lng, residents: group.flatMap((c) => c.residents) };
}

export function isMergedKey(key: string): boolean {
  return key.startsWith("merged:");
}

/** 那一处算哪座城：第一位写了城市的那位（和 regionFocus 同一个认法）；都没写就是 null */
function cityOfCluster(cluster: GlobeCluster): string | null {
  return cluster.residents.map((r) => r.city?.trim()).find((x): x is string => Boolean(x)) ?? null;
}

/** 合成那一盏灯里的居民按城数人（4b，I 10-14）：有城市的按城分，人多的在前（一样多按城名）；没写城市的另算 unplaced。 */
export function citiesOf(residents: readonly PublicMapResident[]): { cities: { city: string; count: number }[]; unplaced: number } {
  const counts = new Map<string, number>();
  let unplaced = 0;
  for (const resident of residents) {
    const city = resident.city?.trim();
    if (city) counts.set(city, (counts.get(city) ?? 0) + 1);
    else unplaced += 1;
  }
  const cities = [...counts].map(([city, count]) => ({ city, count })).sort((a, b) => b.count - a.count || a.city.localeCompare(b.city, "zh-CN"));
  return { cities, unplaced };
}

/**
 * 进某座城的地区层（4b）：镜头中心放在这座城离它几何中心最近的那一处——regionFocus 按离中心最近的那处认城，邻城挨得再近也不会认错；
 * 跨度装下这座城的每一处（各边多留三成，最少 0.1°，和点新鲜事进片区同一个起点）。这座城一处都没有就是 null。
 */
export function cityRegion(clusters: readonly GlobeCluster[], city: string, coordSystem: string): GlobeRegion | null {
  const mine = clusters.filter((cluster) => cityOfCluster(cluster) === city);
  if (!mine.length) return null;
  const lat = mine.reduce((sum, c) => sum + c.lat, 0) / mine.length;
  const lng = mine.reduce((sum, c) => sum + c.lng, 0) / mine.length;
  const off = (c: LatLng) => Math.hypot(c.lat - lat, c.lng - lng);
  const anchor = mine.reduce((best, c) => (off(c) < off(best) ? c : best));
  const reach = (pick: (c: LatLng) => number) => Math.max(...mine.map((c) => Math.abs(pick(c) - pick(anchor))));
  return {
    center: { lat: anchor.lat, lng: anchor.lng },
    latSpan: Math.max(0.1, reach((c) => c.lat) * 2 * 1.3),
    lngSpan: Math.max(0.1, reach((c) => c.lng) * 2 * 1.3),
    coordSystem,
  };
}

/** 合成那一处的 key 里记着的各处 key（第一处是画标记的那一处） */
export function mergedKeys(key: string): string[] {
  return isMergedKey(key) ? key.slice("merged:".length).split("|") : [key];
}
